import { GantWP } from './GantWP';
import { tokens } from '../../../global/theme/tokens';
import { WorkPackageForm } from '../project/workPackage/WorkPackageForm';
import { createElement, Fragment, useEffect, useState } from 'react';
import { updateOrCreate } from '../../special/updateOrCreate';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { GantPopper } from './GantPopper';
import { useTranslation } from 'react-i18next';
import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import FlagIcon from '@mui/icons-material/Flag';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import DateLabel from '../../label/DateLabel';
import { useTimeTracking } from '../../time-tracking/AppTimeProvider';
import { ProjectUserStack } from '../project/ProjectUserStack';
import { styled } from '@mui/material/styles';
import LinearProgress, {
  linearProgressClasses,
} from '@mui/material/LinearProgress';
import moment from 'moment';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { NavLink } from 'react-router-dom';
import { renderHours } from '../../../scenes/time-tracking/worktime';
import { Headset } from '@mui/icons-material';

export const GantPhase = ({
  phase,
  isFirst,
  dayWidth,
  onChange,
  days,
  inEditMode,
  projectId,
  resolution,
  allowedStatuses,
  editable,
  infoWidth = 0, // Width for additional info column
  latestStart,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const { t } = useTranslation();

  const { name, workPackages, project, heat } = phase;
  const [showPackages, setShowPackages] = useState(true);

  const [showWP, setShowWP] = useState(null);
  const [editWP, setEditWP] = useState(null);

  const [expandedWpId, setExpandedWpId] = useState(null);

  function handleUpdateWorkPackage(updatedWP) {
    const newWorkPackages = updateOrCreate(workPackages, updatedWP);
    onChange({ ...phase, workPackages: newWorkPackages });
  }

  const handleClick = (event, wp) => {
    const clickX = event.clientX;
    const clickY = event.clientY - 20;
    const virtualAnchor = {
      getBoundingClientRect: () => ({
        top: clickY,
        left: clickX,
        bottom: clickY,
        right: clickX,
        width: 0,
        height: 0,
        x: clickX,
        y: clickY,
        toJSON: () => {},
      }),
    };

    setShowWP({ wpId: wp.id, anchor: virtualAnchor });
  };

  const statusMap = {
    0: {
      label: t('Running'),
      Icon: DirectionsRunIcon,
      color: colors.greenAccent[600],
      opacity: 1,
    },
    1: {
      label: t('Finished'),
      Icon: FlagIcon,
      color: colors.grey[600],
      opacity: 1,
    },
    2: {
      label: t('Infinite'),
      Icon: AllInclusiveIcon,
      color: colors.blueAccent[600],
      opacity: 1,
    },
    3: {
      label: t('Canceled'),
      Icon: ReportGmailerrorredIcon,
      color: colors.redAccent[600],
      opacity: 1,
    },
  };

  const { clockIn, clockOut, activeProject, secondsIn } = useTimeTracking();

  const sortedWorkPackages = [...workPackages].sort((a, b) => {
    if (!a.endDate) return 1;
    if (!b.endDate) return -1;

    const dateA = moment(a.endDate);
    const dateB = moment(b.endDate);

    if (dateA.isBefore(dateB)) return -1;
    if (dateA.isAfter(dateB)) return 1;
    return 0;
  });

  // should the phase be hidden? no active workpackages?
  const [count, setCount] = useState(0);
  function calculateShownWorkPackageCount() {
    var newCount = 0;
    workPackages.forEach((wp) => {
      if (allowedStatuses.includes(wp.status)) newCount++;
    });
    return newCount;
  }

  useEffect(() => {
    setCount(calculateShownWorkPackageCount());
  }, [phase, allowedStatuses]);

  if (count === 0) return;
  return (
    <Fragment>
      <tr className="hidden lg:table-row">
        <td
          style={{
            backgroundColor: colors.glass,
            width: infoWidth,
            maxWidth: infoWidth,
          }}
          className="sticky left-0 backdrop-blur-sm hidden lg:table-cell  "
        >
          <Box
            className="pb-1 pt-4"
            sx={{ borderTop: `1px solid ${colors.card}` }}
          >
            <Typography
              fontWeight={600}
              className="pl-2 pt-[1px] cursor-pointer w-full max-w-full text-ellipsis whitespace-nowrap overflow-hidden"
              onClick={() => setShowPackages(!showPackages)}
            >
              <ArrowDropDownIcon
                sx={{
                  transform: `rotate(${showPackages ? 180 : 0}deg)`,
                }}
                className="mr-1"
              />
              {name}
            </Typography>

            {project && showPackages && (
              <NavLink to={`/projects/management/${project.id}`}>
                <Box className="pl-[29px] flex items-center gap-2">
                  {project.image ? (
                    <img
                      className="w-[25px] h-[25px] rounded-md object-cover"
                      src={project.image?.temporaryUrl}
                    />
                  ) : (
                    <Box
                      className="w-[25px] h-[25px] rounded-md flex items-center justify-center"
                      style={{
                        backgroundColor: colors.card,
                      }}
                    >
                      <AccountTreeIcon sx={{ fontSize: '15px' }} />
                    </Box>
                  )}
                  <Typography className="w-full text-ellipsis whitespace-nowrap overflow-hidden">
                    {project.name}
                  </Typography>
                </Box>
              </NavLink>
            )}

            <WorkPackageForm
              phaseId={phase.id}
              open={Boolean(editWP)}
              onClose={() => setEditWP(null)}
              startDataCollection={editWP}
              onChange={handleUpdateWorkPackage}
              projectId={projectId}
            />
            <GantPopper
              open={Boolean(showWP)}
              anchorEl={showWP?.anchor}
              workPackage={
                showWP
                  ? phase.workPackages.find((wp) => wp.id === showWP.wpId)
                  : null
              }
              projectId={projectId}
              onClose={() => setShowWP(null)}
              inEditMode={inEditMode}
              onEdit={setEditWP}
              onChange={handleUpdateWorkPackage}
            />
          </Box>
        </td>
        <td
          style={{ backgroundColor: colors.glass }}
          className="min-w-[10px] w-[10px] hidden lg:table-cell"
        ></td>
        <td></td>
      </tr>
      {sortedWorkPackages
        ?.filter((wp) => allowedStatuses?.includes(wp.status))
        .map((workPackage) => {
          const activeWorkpackage =
            activeProject &&
            activeProject.activeWorkPackage &&
            activeProject.activeWorkPackage.id === workPackage.id
              ? {
                  ...activeProject.activeWorkPackage,
                  totalTimeWorkedSeconds: secondsIn
                    ? activeProject.activeWorkPackage.totalTimeWorkedSeconds +
                      secondsIn
                    : activeProject.activeWorkPackage.totalTimeWorkedSeconds,
                }
              : null;

          const workPercentage = activeWorkpackage
            ? activeWorkpackage.totalTimeWorkedSeconds /
              activeWorkpackage.estimatedTime
            : workPackage.totalTimeWorkedSeconds / workPackage.estimatedTime;

          const workedTime =
            (activeWorkpackage
              ? activeWorkpackage.totalTimeWorkedSeconds
              : workPackage.totalTimeWorkedSeconds) / 60;

          const isAfterDeadline =
            workPackage.status === 0 &&
            moment(workPackage.endDate).isBefore(moment(), 'day');

          return (
            <tr
              className={showPackages ? 'table-row' : 'hidden'}
              key={workPackage.id}
            >
              <td
                style={{
                  width: infoWidth,
                  minWidth: infoWidth,
                  backgroundColor: colors.glass,
                }}
                className=" sticky left-0 z-10 backdrop-blur-sm hidden lg:table-cell"
              >
                <Box
                  sx={{
                    borderLeft: `3px solid ${
                      isAfterDeadline ? colors.error : 'transparent'
                    }`,
                    borderRight: `3px solid ${
                      isAfterDeadline ? colors.error : 'transparent'
                    }`,
                    bgcolor: isAfterDeadline
                      ? colors.error + '20'
                      : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    paddingLeft: 1,
                    minHeight: 45,
                  }}
                >
                  <Box
                    component="img"
                    src={workPackage.icon?.temporaryUrl}
                    alt={`${workPackage.title} icon`}
                    sx={{
                      width: 40,
                      height: 45,
                      objectFit: 'contain',
                      flexShrink: 0,
                      alignSelf: 'flex-start',
                    }}
                  />

                  <Box
                    sx={{
                      maxWidth: 330,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                    className="w-full"
                  >
                    <Box
                      sx={{
                        minHeight: 55,
                      }}
                      className="w-full flex items-start cursor-pointer max-w-full overflow-hidden justify-between"
                      onClick={() =>
                        setExpandedWpId(
                          expandedWpId === workPackage.id
                            ? null
                            : workPackage.id
                        )
                      }
                    >
                      <Box className="flex gap-1 items-center w-full h-[47px]">
                        {createElement(statusMap[workPackage.status].Icon, {
                          sx: {
                            color: statusMap[workPackage.status]?.color,
                            mr: 0.5,
                            flexShrink: 0,
                          },
                        })}

                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            flexGrow: 1,
                            minWidth: 0,
                          }}
                          className="w-full py-[5px]"
                        >
                          <Typography
                            fontWeight={600}
                            noWrap
                            sx={{
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                            }}
                            className="w-full"
                          >
                            {workPackage.title}
                          </Typography>
                          <Typography
                            fontSize={9}
                            fontWeight={500}
                            className="h-0 opacity-50"
                          >
                            {workPackage.subTitle}
                          </Typography>

                          <Typography
                            fontWeight={500}
                            className="opacity-60 absolute top-1 right-2"
                            fontSize={10}
                            sx={{ textAlign: 'left' }}
                          >
                            {workPackage.startDate ? (
                              <DateLabel date={workPackage.startDate} />
                            ) : (
                              ''
                            )}
                            {workPackage.endDate ? (
                              <>
                                {' - '}
                                <DateLabel date={workPackage.endDate} />
                              </>
                            ) : (
                              ''
                            )}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {expandedWpId === workPackage.id && (
                      <Box
                        className="gap-2 flex flex-col items-start pl-6 py-2"
                        sx={{
                          '.MuiAvatar-root': {
                            width: '30px',
                            height: '30px',
                          },
                        }}
                      >
                        {Boolean(workPercentage) &&
                          workPercentage !== Infinity && (
                            <Box
                              sx={{ flexShrink: 0 }}
                              className="w-full flex flex-col"
                            >
                              <BorderLinearProgress
                                variant="determinate"
                                value={Math.min(
                                  Math.round(workPercentage * 100),
                                  100
                                )}
                                className="w-full "
                                sx={{
                                  height: '5px',
                                }}
                              />

                              <Typography
                                variant="caption"
                                sx={{ mt: 0.5 }}
                                color="text.secondary"
                              >
                                {Math.round(workPercentage * 100)}% (
                                {renderHours(workedTime)}h
                                {workPackage.estimatedTime
                                  ? ` / ${renderHours(
                                      workPackage.estimatedTime / 60
                                    )}h`
                                  : ''}
                                )
                              </Typography>
                            </Box>
                          )}
                        <Box className="team cursor-pointer">
                          <ProjectUserStack
                            editmode={inEditMode}
                            users={workPackage.users}
                            workPackage={workPackage}
                            onChange={handleUpdateWorkPackage}
                            projectId={projectId}
                            noLabel
                            size="20px"
                            noEmptyIcon
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              </td>
              <td
                className="min-w-[10px] hidden lg:table-cell"
                style={{ backgroundColor: colors.glass }}
              ></td>
              <td className="h-[55px]">
                <GantWP
                  key={workPackage.id}
                  workPackage={workPackage}
                  dayWidth={dayWidth}
                  onEdit={(data) => {
                    setEditWP(data);
                    setShowWP(null);
                  }}
                  onShow={handleClick}
                  days={days}
                  inEditMode={inEditMode}
                  projectId={projectId}
                  onChange={handleUpdateWorkPackage}
                  resolution={resolution}
                  allowedStatuses={allowedStatuses}
                  editable={editable}
                  infoWidth={infoWidth}
                />
              </td>
            </tr>
          );
        })}
    </Fragment>
  );
};

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 8,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[300],
    ...theme.applyStyles?.('dark', {
      backgroundColor: theme.palette.grey[600],
    }),
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: '#1a90ff',
    ...theme.applyStyles?.('dark', {
      backgroundColor: '#308fe8',
    }),
  },
}));
